import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  message,
  Modal,
  Select,
  Table,
  Tag,
  DatePicker
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import transactions from "@/core/transactions/transactions";
import ShowDocument from "@/components/invoices/ShowDocument";
import { useTranslation } from "react-i18next";
import NumberRound from "number-zero-round";

const { RangePicker } = DatePicker;
const { Option } = Select;

const GREEN_COLOR = '#D4F4D9';
const RED_COLOR = '#FADEE3';

const PackingSlipTable = () => {
  const [selectedDocumentNumber, setSelectedDocumentNumber] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [form] = Form.useForm();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [documentNumber, setDocumentNumber] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();

  const onChangePerSizeHandler = (value) => {
    setPageSize(value);
    fetchData({ page: 1, limit: value });
  };

  const onFinish = () => {
    const params = {
      currPage: 1,
      limit: pageSize,
      ...(startDate && { startDate: startDate.format('YYYY-MM-DD HH:mm:ss') }),
      ...(endDate && { endDate: endDate.format('YYYY-MM-DD HH:mm:ss') }),
      ...(documentNumber && { documentNumber }),
    };
    setPage(1);
    fetchData(params);
  };

  const fetchData = (params) => {
    setLoading(true);
    transactions.fetchSlips({
      page: params?.currPage || page,
      limit: params?.limit || pageSize,
      ...params,
    })
      .then((res) => setData(res.data.data))
      .catch((err) => {
        message.error(err?.response?.data?.message || t('error-fetch-failed'));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns = [
    {
      title: t("invoice-number"),
      dataIndex: "DocumentNumber",
      key: "DocumentNumber",
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.DocumentNumber - b.DocumentNumber,
    },
    {
      title: t("invoice-date"),
      dataIndex: "DocumentDate",
      key: "DocumentDate",
      sorter: (a, b) => moment(a.DocumentDate).unix() - moment(b.DocumentDate).unix(),
      render: (value) => moment(value).format("DD/MM/YYYY"),
    },
    {
      title: t("total-net-price"),
      dataIndex: "TotalNetPrice",
      key: "TotalNetPrice",
      sorter: (a, b) => a.TotalNetPrice - b.TotalNetPrice,
      render: (value) => `€${NumberRound(value)}`,
    },
    {
      title: t("total-amount"),
      dataIndex: "TotalAmount",
      key: "TotalAmount",
      sorter: (a, b) => a.TotalAmount - b.TotalAmount,
      render: (value) => `€${NumberRound(value)}`,
    },
    {
      title: t("status"),
      dataIndex: "Open",
      key: "Open",
      sorter: (a, b) => a?.Status - b?.Status,
      render: (_, record) =>
        Number(record.Status) === 0
          ? <div className="flex justify-center items-center">{t("close")}</div>
          : <div className="flex justify-center items-center">{t("open")}</div>,
    },
    {
      title: t("old-days"),
      key: "OldDays",
      render: (_, record) => (
        <div className="text-center">
          {record.Status === 0 ? "-" : record.OldDays}
        </div>
      ),
    },
    {
      title: "",
      key: "download",
      render: (_, record) => (
        <div
          onClick={() => {
            setSelectedDocumentNumber(record.DocumentNumber);
            setShowDocumentModal(true);
          }}
        >
          <DownloadOutlined style={{ fontSize: "19px", color: "var(--primaryColor)", cursor: "pointer" }} />
        </div>
      ),
    },
  ];

  return (
    <div>
      <Modal
        bodyStyle={{ height: "1000px", padding: 0 }}
        open={showDocumentModal}
        footer={null}
        width="100%"
        onCancel={() => setShowDocumentModal(false)}
      >
        <ShowDocument documentNumber={selectedDocumentNumber} type={2} />
      </Modal>

      <Form layout="vertical" form={form}>
        <Form.Item name="invoiceNumber" label={t("invoice-number")}>
          <Input value={documentNumber} onChange={(e) => setDocumentNumber(e.target.value)} />
        </Form.Item>

        <Form.Item label={t("date")}>
          <RangePicker
            showTime
            onChange={(value) => {
              if (value) {
                setStartDate(value[0]);
                setEndDate(value[1]);
              } else {
                setStartDate(null);
                setEndDate(null);
              }
            }}
          />
        </Form.Item>

        <Form.Item>
          <button onClick={onFinish} className="mega-button">
            {t("search")}
          </button>
        </Form.Item>
      </Form>

      <Table
        pagination={false}
        size="small"
        loading={loading}
        dataSource={data}
        columns={columns}
        summary={(pageData) => {
          const totalNet = data.reduce((acc, item) => acc + parseFloat(item?.TotalNetPrice || 0), 0);
          const totalAmount = data.reduce((acc, item) => acc + parseFloat(item?.TotalAmount || 0), 0);
          return (
            <Table.Summary.Row style={{ backgroundColor: "#F2F5F6" }}>
              <Table.Summary.Cell>{t("total")}</Table.Summary.Cell>
              <Table.Summary.Cell />
              <Table.Summary.Cell><Tag color="blue">{NumberRound(totalNet)}</Tag></Table.Summary.Cell>
              <Table.Summary.Cell><Tag color="orange">{NumberRound(totalAmount)}</Tag></Table.Summary.Cell>
              <Table.Summary.Cell colSpan={4} />
            </Table.Summary.Row>
          );
        }}
        title={() => (
          <div>
            <span>{t("show-per-page")}:</span>
            <Select
              style={{ width: 200, marginLeft: 10 }}
              value={pageSize}
              onChange={onChangePerSizeHandler}
            >
              <Option value={10}>10</Option>
              <Option value={50}>50</Option>
              <Option value={100}>100</Option>
              <Option value={500}>500</Option>
            </Select>
          </div>
        )}
        onRow={(record) => ({
          style: {
            background: record?.Status === 1 ? RED_COLOR : GREEN_COLOR,
          },
        })}
        className="table-2"
      />
    </div>
  );
};

export default PackingSlipTable;
